import React, { useState, useEffect } from "react";
import { Bell, TrendDown, Warning, Fire, CalendarX, Info, ArrowsClockwise, PencilSimple, Check, X, TrendUp, Stack, Target, CalendarCheck, Scales, CaretRight, CaretDown, Timer, Lock, EnvelopeSimple } from "phosphor-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useLanguage } from "../context/LanguageContext";
import { useSharedProfile } from "../context/SharedProfileContext";
import api from "../lib/api";
import { toast } from "sonner";

// Footer branding
const LOGO_PUREBRAIN = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/gtg4vc6a_purebrain.png";

// Local storage key for alert settings
const ALERT_SETTINGS_KEY = 'geni_alert_settings';

// Default alert settings
const DEFAULT_SETTINGS = {
  losing_streak: { enabled: false, threshold: 3 },
  daily_drawdown: { enabled: false, threshold: 5 },
  weekly_drawdown: { enabled: false, threshold: 10 },
  overtrading: { enabled: false, threshold: 10 },
  revenge_trade: { enabled: false, threshold: 2 }, // threshold in hours
  winning_streak: { enabled: false, threshold: 3 }, // threshold in days
  max_open_positions: { enabled: false, threshold: 5 }, // threshold in positions
  daily_target: { enabled: false, threshold: 2 }, // threshold in percent
  weekly_target: { enabled: false, threshold: 5 }, // threshold in percent
  directional_exposure: { enabled: false, threshold: 60 }, // threshold in percent bias
  capital_drop: { enabled: false, threshold: 10 }, // threshold in percent drop from initial capital
  hold_time_drift: { 
    enabled: false, 
    trade_type: 'both', // 'day_trade', 'swing_trade', 'both'
    alert_near_win: true, // alert near average WIN hold time
    alert_past_loss: true, // alert past average LOSS hold time
    show_badge: true // show visual badge in Open Positions
  }
};

// Load settings from localStorage
const loadSettings = () => {
  try {
    const stored = localStorage.getItem(ALERT_SETTINGS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to ensure all keys exist
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch (e) {
    console.error('Error loading alert settings:', e);
  }
  return DEFAULT_SETTINGS;
};

// Save settings to localStorage
const saveSettings = (settings) => {
  try {
    localStorage.setItem(ALERT_SETTINGS_KEY, JSON.stringify(settings));
  } catch (e) {
    console.error('Error saving alert settings:', e);
  }
};

// Alert types with backend/email persistence (mirrors trades_alerts_service.TRADES_ALERT_TYPES).
// hold_time_drift is intentionally excluded - stays localStorage/frontend-only.
const BACKEND_ALERT_TYPES = [
  'losing_streak', 'daily_drawdown', 'weekly_drawdown', 'overtrading', 'revenge_trade',
  'winning_streak', 'max_open_positions', 'daily_target', 'weekly_target',
  'directional_exposure', 'capital_drop',
];

export default function Alerts() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null); // which alert is being edited
  const [editValue, setEditValue] = useState(''); // temporary edit value
  const { t, language } = useLanguage();

  // Trades Alerts email settings (backend-persisted master switch + recipient)
  const [masterEmailEnabled, setMasterEmailEnabled] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [savingMaster, setSavingMaster] = useState(false);
  const [testingAlert, setTestingAlert] = useState(null); // alert_type, or 'master', or null
  
  // Shared/Public profile mode - when viewing another user's profile
  const { isSharedProfileView, isReadOnly } = useSharedProfile();
  
  // Collapsible group states - Risk Control open by default
  const [expandedGroups, setExpandedGroups] = useState({
    riskControl: true,
    behavioral: false,
    targets: false
  });

  // Toggle group expansion
  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Load settings on mount - localStorage first (instant), then sync from backend (source of
  // truth for the 11 backend-tracked alerts when present; hold_time_drift stays local-only)
  useEffect(() => {
    const loaded = loadSettings();
    setSettings(loaded);

    if (isSharedProfileView) return;

    (async () => {
      try {
        const response = await api.get('/trades-alerts/settings');
        const backendAlerts = response.data?.alerts;
        if (backendAlerts) {
          setSettings((prev) => {
            const merged = { ...prev };
            Object.keys(backendAlerts).forEach((key) => {
              merged[key] = { ...merged[key], ...backendAlerts[key] };
            });
            saveSettings(merged);
            return merged;
          });
        }
        setMasterEmailEnabled(!!response.data?.master_email_enabled);
        setRecipientEmail(response.data?.recipient_email || '');
      } catch (e) {
        console.error('Error loading trades alert email settings:', e);
      }
    })();
  }, [isSharedProfileView]);

  // Update a specific alert setting - blocked in shared profile mode
  const updateSetting = (alertType, field, value) => {
    if (isSharedProfileView) return; // Block modifications
    
    const newSettings = {
      ...settings,
      [alertType]: {
        ...settings[alertType],
        [field]: value
      }
    };
    setSettings(newSettings);
    saveSettings(newSettings);
    setHasChanges(true);
    
    // Auto-hide the saved indicator after 2 seconds
    setTimeout(() => setHasChanges(false), 2000);

    // Persist enabled/threshold/email_enabled to the backend for the 11 email-enabled
    // alerts (best-effort, non-blocking). hold_time_drift never leaves localStorage.
    if (BACKEND_ALERT_TYPES.includes(alertType)) {
      api.put('/trades-alerts/settings', { alerts: { [alertType]: { [field]: value } } }).catch((e) => {
        console.error('Error persisting alert setting to backend:', e);
      });
    }
  };

  // Global kill switch for Trades Alerts email - does NOT affect in-app alerts/badges
  const toggleMasterEmail = async () => {
    if (isSharedProfileView) return;
    const newValue = !masterEmailEnabled;
    setSavingMaster(true);
    try {
      const response = await api.put('/trades-alerts/settings/master-email', { enabled: newValue });
      setMasterEmailEnabled(!!response.data?.master_email_enabled);
      toast.success(
        newValue
          ? (language === 'ptBR' ? 'Alertas por email ativados' : 'Email alerts enabled')
          : (language === 'ptBR' ? 'Alertas por email desativados' : 'Email alerts disabled')
      );
    } catch (e) {
      console.error('Error updating master email switch:', e);
      toast.error(language === 'ptBR' ? 'Erro ao atualizar' : 'Failed to update');
    } finally {
      setSavingMaster(false);
    }
  };

  // Sends one test email (own address only, [TEST] subject, never touches dedupe/rearm)
  const sendTestAlertEmail = async (alertType) => {
    setTestingAlert(alertType);
    try {
      const response = await api.post(`/trades-alerts/test/${alertType}`);
      if (response.data?.success) {
        toast.success(language === 'ptBR' ? `Email de teste enviado para ${recipientEmail}` : `Test email sent to ${recipientEmail}`);
      } else {
        toast.error(language === 'ptBR' ? 'Falha ao enviar email de teste' : 'Failed to send test email');
      }
    } catch (e) {
      console.error('Error sending test alert email:', e);
      toast.error(language === 'ptBR' ? 'Falha ao enviar email de teste' : 'Failed to send test email');
    } finally {
      setTestingAlert(null);
    }
  };

  const sendMasterTestEmail = async () => {
    setTestingAlert('master');
    try {
      const response = await api.post('/trades-alerts/test-master');
      if (response.data?.success) {
        toast.success(language === 'ptBR' ? `Email de teste enviado para ${recipientEmail}` : `Test email sent to ${recipientEmail}`);
      } else {
        toast.error(language === 'ptBR' ? 'Falha ao enviar email de teste' : 'Failed to send test email');
      }
    } catch (e) {
      console.error('Error sending master test email:', e);
      toast.error(language === 'ptBR' ? 'Falha ao enviar email de teste' : 'Failed to send test email');
    } finally {
      setTestingAlert(null);
    }
  };

  // Enter edit mode for a threshold
  const startEditing = (alertId, currentValue) => {
    setEditingAlert(alertId);
    setEditValue(currentValue.toString());
  };

  // Confirm edit and save
  const confirmEdit = (alertId, min, max) => {
    const val = parseFloat(editValue);
    if (!isNaN(val) && val >= min && val <= max) {
      updateSetting(alertId, 'threshold', val);
    }
    setEditingAlert(null);
    setEditValue('');
  };

  // Cancel edit without saving
  const cancelEdit = () => {
    setEditingAlert(null);
    setEditValue('');
  };

  // Format threshold display text
  const formatThreshold = (alertId, threshold) => {
    switch (alertId) {
      case 'losing_streak':
        return `${threshold} consecutive loss${threshold !== 1 ? 'es' : ''}`;
      case 'daily_drawdown':
      case 'weekly_drawdown':
        return `${threshold}% of capital`;
      case 'overtrading':
        return `${threshold} trade${threshold !== 1 ? 's' : ''} per day`;
      case 'revenge_trade':
        return `${threshold} hour${threshold !== 1 ? 's' : ''}`;
      case 'winning_streak':
        return `${threshold} consecutive winning day${threshold !== 1 ? 's' : ''}`;
      case 'max_open_positions':
        return `${threshold} open position${threshold !== 1 ? 's' : ''}`;
      case 'daily_target':
        return `${threshold}% daily return`;
      case 'weekly_target':
        return `${threshold}% weekly return`;
      case 'directional_exposure':
        return `${threshold}% directional bias`;
      case 'capital_drop':
        return `${threshold}% drop from initial`;
      default:
        return threshold.toString();
    }
  };

  // Alert configuration data - organized by group
  
  // Group 1: Risk Control alerts
  const riskControlAlerts = [
    {
      id: 'daily_drawdown',
      title: 'Max Daily Drawdown',
      description: 'Alert when your daily losses exceed a percentage of capital',
      icon: Warning,
      iconColor: 'text-[#FF9500]',
      bgColor: 'bg-[#FF9500]/5',
      borderColor: 'border-[#FF9500]/20',
      unit: '% of capital',
      min: 1,
      max: 20
    },
    {
      id: 'weekly_drawdown',
      title: 'Max Weekly Drawdown',
      description: 'Alert when your weekly losses exceed a percentage of capital',
      icon: CalendarX,
      iconColor: 'text-[#FF6B00]',
      bgColor: 'bg-[#FF6B00]/5',
      borderColor: 'border-[#FF6B00]/20',
      unit: '% of capital',
      min: 2,
      max: 30
    },
    {
      id: 'overtrading',
      title: 'Max Trades Per Day',
      description: 'Alert when you exceed a set number of trades in a single day',
      icon: Fire,
      iconColor: 'text-[#FF3B30]',
      bgColor: 'bg-[#FF3B30]/5',
      borderColor: 'border-[#FF3B30]/20',
      unit: 'trades per day',
      min: 1,
      max: 50
    },
    {
      id: 'max_open_positions',
      title: 'Max Open Positions',
      description: 'Alert when your number of open positions becomes too high',
      icon: Stack,
      iconColor: 'text-[#FF9500]',
      bgColor: 'bg-[#FF9500]/5',
      borderColor: 'border-[#FF9500]/20',
      unit: 'open positions',
      min: 1,
      max: 20
    },
    {
      id: 'directional_exposure',
      title: 'Directional Exposure',
      description: 'Alert when your open positions are too concentrated on one side of the market',
      icon: Scales,
      iconColor: 'text-[#FF9500]',
      bgColor: 'bg-[#FF9500]/5',
      borderColor: 'border-[#FF9500]/20',
      unit: '% directional bias',
      min: 40,
      max: 90,
      step: 5
    },
    {
      id: 'capital_drop',
      title: 'Capital Drop',
      description: 'Alert when your current capital drops below a percentage of your starting capital',
      icon: TrendDown,
      iconColor: 'text-[#FF3B30]',
      bgColor: 'bg-[#FF3B30]/5',
      borderColor: 'border-[#FF3B30]/20',
      unit: '% drop from initial',
      min: 0.5,
      max: 50,
      step: 0.5
    }
  ];

  // Group 2: Behavioral alerts
  const behavioralAlerts = [
    {
      id: 'losing_streak',
      title: 'Losing Streak',
      description: 'Alert when you hit consecutive losing trades',
      icon: TrendDown,
      iconColor: 'text-[#FF3B30]',
      bgColor: 'bg-[#FF3B30]/5',
      borderColor: 'border-[#FF3B30]/20',
      unit: 'consecutive losses',
      min: 2,
      max: 10
    },
    {
      id: 'winning_streak',
      title: 'Winning Streak',
      description: 'Alert when you have consecutive profitable trading days',
      icon: TrendUp,
      iconColor: 'text-[#00E676]',
      bgColor: 'bg-[#00E676]/5',
      borderColor: 'border-[#00E676]/20',
      unit: 'consecutive winning days',
      min: 2,
      max: 10
    },
    {
      id: 'revenge_trade',
      title: 'Revenge Trade',
      description: 'Alert when you re-enter the same asset too soon after a losing trade',
      icon: ArrowsClockwise,
      iconColor: 'text-[#AF52DE]',
      bgColor: 'bg-[#AF52DE]/5',
      borderColor: 'border-[#AF52DE]/20',
      unit: 'hours after loss',
      min: 1,
      max: 24
    },
    {
      id: 'hold_time_drift',
      title: 'Hold-Time Drift',
      description: 'Alert when open positions exceed your historical hold time patterns',
      icon: Timer,
      iconColor: 'text-[#5AC8FA]',
      bgColor: 'bg-[#5AC8FA]/5',
      borderColor: 'border-[#5AC8FA]/20',
      isCustom: true // Flag to render custom controls instead of threshold
    }
  ];

  // Group 3: Target alerts
  const targetAlerts = [
    {
      id: 'daily_target',
      title: 'Daily Target Reached',
      description: 'Alert when your daily profit target is reached',
      icon: Target,
      iconColor: 'text-[#30D158]',
      bgColor: 'bg-[#30D158]/5',
      borderColor: 'border-[#30D158]/20',
      unit: '% daily return',
      min: 0.5,
      max: 20,
      step: 0.5
    },
    {
      id: 'weekly_target',
      title: 'Weekly Target Reached',
      description: 'Alert when your weekly profit target is reached',
      icon: CalendarCheck,
      iconColor: 'text-[#30D158]',
      bgColor: 'bg-[#30D158]/5',
      borderColor: 'border-[#30D158]/20',
      unit: '% weekly return',
      min: 1,
      max: 30,
      step: 1
    }
  ];

  // Helper function to render an alert card
  const renderAlertCard = (alert) => {
    const AlertIcon = alert.icon;
    const isEnabled = settings[alert.id]?.enabled || false;
    const threshold = settings[alert.id]?.threshold || DEFAULT_SETTINGS[alert.id]?.threshold;

    // Special rendering for Hold-Time Drift alert
    if (alert.id === 'hold_time_drift') {
      return renderHoldTimeDriftCard(alert);
    }

    return (
      <div
        key={alert.id}
        className={`p-5 rounded-lg border transition-all ${
          isEnabled 
            ? `${alert.bgColor} ${alert.borderColor}` 
            : 'bg-[#121212] border-white/10'
        }`}
        data-testid={`alert-card-${alert.id}`}
      >
        <div className="flex items-start justify-between gap-4">
          {/* Left: Icon + Info */}
          <div className="flex items-start gap-4 flex-1">
            <div className={`p-2.5 rounded-lg ${isEnabled ? alert.bgColor : 'bg-white/5'}`}>
              <AlertIcon 
                size={24} 
                weight="duotone" 
                className={isEnabled ? alert.iconColor : 'text-white/30'} 
              />
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold text-base mb-1 ${isEnabled ? 'text-white' : 'text-white/60'}`}>
                {alert.title}
              </h3>
              <p className="text-white/40 text-sm">
                {alert.description}
              </p>
              
              {/* Threshold - Only visible when enabled */}
              {isEnabled && (
                <div className="mt-3 flex items-center gap-2">
                  {editingAlert === alert.id ? (
                    // Edit Mode
                    <div className="flex items-center gap-2">
                      <span className="text-white/50 text-sm">Threshold:</span>
                      <Input
                        type="number"
                        min={alert.min}
                        max={alert.max}
                        step={alert.step || 1}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') confirmEdit(alert.id, alert.min, alert.max);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        autoFocus
                        className="w-16 h-7 bg-[#0A0A0A] border-white/30 text-white text-center text-sm px-2"
                        data-testid={`alert-threshold-input-${alert.id}`}
                      />
                      <button
                        onClick={() => confirmEdit(alert.id, alert.min, alert.max)}
                        className="p-1 rounded hover:bg-[#00E676]/20 text-[#00E676] transition-colors"
                        data-testid={`alert-threshold-confirm-${alert.id}`}
                      >
                        <Check size={16} weight="bold" />
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="p-1 rounded hover:bg-white/10 text-white/50 transition-colors"
                        data-testid={`alert-threshold-cancel-${alert.id}`}
                      >
                        <X size={16} weight="bold" />
                      </button>
                    </div>
                  ) : (
                    // Display Mode
                    <button
                      onClick={() => startEditing(alert.id, threshold)}
                      className="group flex items-center gap-2 text-sm text-white/50 hover:text-white/70 transition-colors"
                      data-testid={`alert-threshold-${alert.id}`}
                    >
                      <span>Threshold:</span>
                      <span className="text-white/80 font-medium">
                        {formatThreshold(alert.id, threshold)}
                      </span>
                      <PencilSimple 
                        size={14} 
                        weight="bold" 
                        className="opacity-0 group-hover:opacity-100 transition-opacity" 
                      />
                    </button>
                  )}
                </div>
              )}

              {/* Email delivery - independent of the alert's in-app toggle above */}
              {isEnabled && (
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <span className="text-white/40 text-xs">
                    {language === 'ptBR' ? 'Enviar por email' : 'Send by email'}
                  </span>
                  <Switch
                    checked={settings[alert.id]?.email_enabled || false}
                    onCheckedChange={(checked) => updateSetting(alert.id, 'email_enabled', checked)}
                    data-testid={`alert-email-toggle-${alert.id}`}
                    disabled={isSharedProfileView}
                  />
                  {settings[alert.id]?.email_enabled && (
                    <button
                      onClick={() => sendTestAlertEmail(alert.id)}
                      disabled={testingAlert === alert.id || isSharedProfileView}
                      className="text-xs text-white/50 hover:text-white/80 underline transition-colors disabled:opacity-40"
                      data-testid={`alert-email-test-${alert.id}`}
                    >
                      {testingAlert === alert.id
                        ? (language === 'ptBR' ? 'Enviando...' : 'Sending...')
                        : (language === 'ptBR' ? 'Enviar teste' : 'Send test')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: Toggle */}
          <div className="shrink-0">
            <Switch
              checked={isEnabled}
              onCheckedChange={(checked) => updateSetting(alert.id, 'enabled', checked)}
              className={isEnabled ? 'data-[state=checked]:bg-white' : ''}
              data-testid={`alert-toggle-${alert.id}`}
              disabled={isSharedProfileView}
            />
          </div>
        </div>
      </div>
    );
  };

  // Render the Hold-Time Drift alert card with custom controls
  const renderHoldTimeDriftCard = (alert) => {
    const AlertIcon = alert.icon;
    const htdSettings = settings.hold_time_drift || DEFAULT_SETTINGS.hold_time_drift;
    const isEnabled = htdSettings.enabled || false;
    const tradeType = htdSettings.trade_type || 'both';
    const alertNearWin = htdSettings.alert_near_win !== false;
    const alertPastLoss = htdSettings.alert_past_loss !== false;

    return (
      <div
        key={alert.id}
        className={`p-5 rounded-lg border transition-all ${
          isEnabled 
            ? `${alert.bgColor} ${alert.borderColor}` 
            : 'bg-[#121212] border-white/10'
        }`}
        data-testid={`alert-card-${alert.id}`}
      >
        <div className="flex items-start justify-between gap-4">
          {/* Left: Icon + Info */}
          <div className="flex items-start gap-4 flex-1">
            <div className={`p-2.5 rounded-lg ${isEnabled ? alert.bgColor : 'bg-white/5'}`}>
              <AlertIcon 
                size={24} 
                weight="duotone" 
                className={isEnabled ? alert.iconColor : 'text-white/30'} 
              />
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold text-base mb-1 ${isEnabled ? 'text-white' : 'text-white/60'}`}>
                {alert.title}
              </h3>
              <p className="text-white/40 text-sm">
                {alert.description}
              </p>
              
              {/* Custom Controls - Only visible when enabled */}
              {isEnabled && (
                <div className="mt-4 space-y-4">
                  {/* Trade Type Selector */}
                  <div className="flex flex-col gap-2">
                    <span className="text-white/50 text-sm">Trade Type:</span>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: 'day_trade', label: 'Day Trade' },
                        { value: 'swing_trade', label: 'Swing Trade' },
                        { value: 'both', label: 'Both' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => updateSetting('hold_time_drift', 'trade_type', option.value)}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                            tradeType === option.value
                              ? 'bg-[#5AC8FA]/20 text-[#5AC8FA] border border-[#5AC8FA]/40'
                              : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10'
                          }`}
                          data-testid={`hold-time-drift-type-${option.value}`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Alert Sub-Toggles */}
                  <div className="flex flex-col gap-3">
                    <span className="text-white/50 text-sm">Alert Triggers:</span>
                    
                    {/* Near average WIN hold time */}
                    <div className="flex items-center justify-between pl-1">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#FFD60A]" />
                        <span className="text-white/70 text-sm">Near average WIN hold time</span>
                        <span className="text-white/30 text-xs">(soft)</span>
                      </div>
                      <Switch
                        checked={alertNearWin}
                        onCheckedChange={(checked) => updateSetting('hold_time_drift', 'alert_near_win', checked)}
                        className="data-[state=checked]:bg-[#FFD60A]"
                        data-testid="hold-time-drift-alert-near-win"
                        disabled={isSharedProfileView}
                      />
                    </div>

                    {/* Past average LOSS hold time */}
                    <div className="flex items-center justify-between pl-1">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#FF453A]" />
                        <span className="text-white/70 text-sm">Past average LOSS hold time</span>
                        <span className="text-white/30 text-xs">(strong)</span>
                      </div>
                      <Switch
                        checked={alertPastLoss}
                        onCheckedChange={(checked) => updateSetting('hold_time_drift', 'alert_past_loss', checked)}
                        className="data-[state=checked]:bg-[#FF453A]"
                        data-testid="hold-time-drift-alert-past-loss"
                        disabled={isSharedProfileView}
                      />
                    </div>
                  </div>

                  {/* Visual Badge Toggle */}
                  <div className="flex flex-col gap-2 pt-2 border-t border-white/10">
                    <span className="text-white/50 text-sm">Visual Indicator:</span>
                    <div className="flex items-center justify-between pl-1">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#5AC8FA]" />
                        <span className="text-white/70 text-sm">Show badge in Open Positions</span>
                      </div>
                      <Switch
                        checked={htdSettings.show_badge !== false}
                        onCheckedChange={(checked) => updateSetting('hold_time_drift', 'show_badge', checked)}
                        className="data-[state=checked]:bg-[#5AC8FA]"
                        data-testid="hold-time-drift-show-badge"
                        disabled={isSharedProfileView}
                      />
                    </div>
                  </div>

                  {/* Info note */}
                  <div className="mt-2 p-2.5 rounded-md bg-white/5 border border-white/10">
                    <p className="text-white/40 text-xs leading-relaxed">
                      Requires at least 5 closed wins and 5 closed losses for each trade type. 
                      Loss hold time must be ≥1.2× win hold time for alerts to activate.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Toggle */}
          <div className="shrink-0">
            <Switch
              checked={isEnabled}
              onCheckedChange={(checked) => updateSetting('hold_time_drift', 'enabled', checked)}
              className={isEnabled ? 'data-[state=checked]:bg-white' : ''}
              data-testid={`alert-toggle-${alert.id}`}
              disabled={isSharedProfileView}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] p-4 md:p-8" data-testid="alerts-page">
      <div className="w-full max-w-[900px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Bell size={32} weight="duotone" className="text-white/80" />
            <h2 className="text-4xl md:text-5xl font-black font-heading tracking-tight" data-testid="alerts-title">
              {t('alerts.title')}
            </h2>
          </div>
          <p className="text-white/60 text-base font-sans">
            {language === 'ptBR' 
              ? 'Configure alertas para manter a disciplina e proteger seu capital' 
              : 'Configure alerts to stay disciplined and protect your capital'}
          </p>
        </div>

        {/* Info Banner */}
        <div className="mb-8 p-4 rounded-lg bg-white/5 border border-white/10 flex items-start gap-3">
          <Info size={20} weight="fill" className="text-white/40 mt-0.5 shrink-0" />
          <div>
            <p className="text-white/70 text-sm">
              {language === 'ptBR' 
                ? <>Alertas são acionados ao carregar o Dashboard quando as condições são atendidas. Cada alerta dispara <span className="text-white font-medium">uma vez por sessão</span> para evitar spam.</>
                : <>Alerts are triggered when you load the Dashboard and conditions are met. Each alert fires <span className="text-white font-medium">once per session</span> to avoid spam.</>}
            </p>
          </div>
        </div>

        {/* Master Email Switch - global kill switch for Trades Alerts email delivery */}
        <div
          className="mb-8 p-5 rounded-lg bg-white/5 border border-white/10 flex items-start justify-between gap-4 flex-wrap"
          data-testid="trades-alerts-master-email-card"
        >
          <div className="flex items-start gap-3 flex-1 min-w-[240px]">
            <div className={`p-2.5 rounded-lg ${masterEmailEnabled ? 'bg-[#00E676]/10' : 'bg-white/5'}`}>
              <EnvelopeSimple size={24} weight="duotone" className={masterEmailEnabled ? 'text-[#00E676]' : 'text-white/40'} />
            </div>
            <div>
              <h3 className="font-semibold text-base text-white mb-1">
                {language === 'ptBR' ? 'Alertas por email' : 'Email alerts'}
              </h3>
              <p className="text-white/40 text-sm">
                {language === 'ptBR'
                  ? 'Chave geral para envio de emails dos alertas. Não afeta os alertas dentro do app.'
                  : 'Global switch for alert email delivery. Does not affect in-app alerts.'}
              </p>
              {recipientEmail && (
                <p className="text-white/30 text-xs mt-1" data-testid="trades-alerts-recipient-email">
                  {language === 'ptBR' ? 'Enviando para' : 'Sending to'}: <span className="text-white/50">{recipientEmail}</span>
                </p>
              )}
              {masterEmailEnabled && (
                <button
                  onClick={sendMasterTestEmail}
                  disabled={testingAlert === 'master' || isSharedProfileView}
                  className="mt-2 text-xs text-white/50 hover:text-white/80 underline transition-colors disabled:opacity-40"
                  data-testid="trades-alerts-master-test-button"
                >
                  {testingAlert === 'master'
                    ? (language === 'ptBR' ? 'Enviando...' : 'Sending...')
                    : (language === 'ptBR' ? 'Enviar teste geral' : 'Send general test')}
                </button>
              )}
            </div>
          </div>
          <div className="shrink-0">
            <Switch
              checked={masterEmailEnabled}
              onCheckedChange={toggleMasterEmail}
              disabled={savingMaster || isSharedProfileView}
              className={masterEmailEnabled ? 'data-[state=checked]:bg-[#00E676]' : ''}
              data-testid="trades-alerts-master-email-toggle"
            />
          </div>
        </div>

        {/* Save Indicator */}
        {hasChanges && (
          <div className="mb-4 text-sm text-[#00E676] flex items-center gap-2" data-testid="settings-saved">
            <span className="w-2 h-2 bg-[#00E676] rounded-full animate-pulse" />
            {t('settings.changesSaved')}
          </div>
        )}

        {/* Group 1: Risk Control */}
        <div className="mb-6">
          <button
            onClick={() => toggleGroup('riskControl')}
            className="w-full flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/8 transition-colors mb-3"
            data-testid="group-toggle-risk-control"
          >
            <div className="flex items-center gap-3">
              {expandedGroups.riskControl ? (
                <CaretDown size={20} weight="bold" className="text-white/60" />
              ) : (
                <CaretRight size={20} weight="bold" className="text-white/60" />
              )}
              <div className="text-left">
                <h3 className="text-lg font-semibold text-white">{t('alerts.riskControl')}</h3>
                <p className="text-white/40 text-sm">
                  {language === 'ptBR' ? 'Proteja capital, exposição e frequência de trades' : 'Protect capital, exposure, and trading frequency'}
                </p>
              </div>
            </div>
            <span className="text-white/30 text-sm">{riskControlAlerts.length} {language === 'ptBR' ? 'alertas' : 'alerts'}</span>
          </button>
          <div 
            className={`space-y-3 overflow-hidden transition-all duration-300 ${
              expandedGroups.riskControl ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            {riskControlAlerts.map(renderAlertCard)}
          </div>
        </div>

        {/* Group 2: Behavioral Alerts */}
        <div className="mb-6">
          <button
            onClick={() => toggleGroup('behavioral')}
            className="w-full flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/8 transition-colors mb-3"
            data-testid="group-toggle-behavioral"
          >
            <div className="flex items-center gap-3">
              {expandedGroups.behavioral ? (
                <CaretDown size={20} weight="bold" className="text-white/60" />
              ) : (
                <CaretRight size={20} weight="bold" className="text-white/60" />
              )}
              <div className="text-left">
                <h3 className="text-lg font-semibold text-white">{t('alerts.behaviorTracking')}</h3>
                <p className="text-white/40 text-sm">
                  {language === 'ptBR' ? 'Identifique padrões emocionais e de consistência' : 'Spot emotional and consistency patterns in your trading'}
                </p>
              </div>
            </div>
            <span className="text-white/30 text-sm">{behavioralAlerts.length} {language === 'ptBR' ? 'alertas' : 'alerts'}</span>
          </button>
          <div 
            className={`space-y-3 overflow-hidden transition-all duration-300 ${
              expandedGroups.behavioral ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            {behavioralAlerts.map(renderAlertCard)}
          </div>
        </div>

        {/* Group 3: Performance Targets */}
        <div>
          <button
            onClick={() => toggleGroup('targets')}
            className="w-full flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/8 transition-colors mb-3"
            data-testid="group-toggle-targets"
          >
            <div className="flex items-center gap-3">
              {expandedGroups.targets ? (
                <CaretDown size={20} weight="bold" className="text-white/60" />
              ) : (
                <CaretRight size={20} weight="bold" className="text-white/60" />
              )}
              <div className="text-left">
                <h3 className="text-lg font-semibold text-white">{t('alerts.performanceMonitor')}</h3>
                <p className="text-white/40 text-sm">
                  {language === 'ptBR' ? 'Acompanhe quando atingir suas metas de lucro' : 'Track when you hit your profit goals'}
                </p>
              </div>
            </div>
            <span className="text-white/30 text-sm">{targetAlerts.length} {language === 'ptBR' ? 'alertas' : 'alerts'}</span>
          </button>
          <div 
            className={`space-y-3 overflow-hidden transition-all duration-300 ${
              expandedGroups.targets ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            {targetAlerts.map(renderAlertCard)}
          </div>
        </div>
      </div>

      {/* Footer Branding */}
      <div className="flex items-center justify-center gap-2 mt-16 mb-6 text-sm text-white/40">
        <img
          src={LOGO_PUREBRAIN}
          alt="GENI"
          className="h-5 w-auto object-contain opacity-80"
        />
        <span>
          {language === 'ptBR' ? 'Desenvolvido por' : 'Powered by'} <span className="text-white font-medium">GENI</span>
        </span>
      </div>
    </div>
  );
}
